/*
 *   Copyright (c) 2026 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import styled from "styled-components";
import { Badge, Button } from "react-bootstrap";

export const TicketsContainer = styled.div`
  width: 100%;
  margin-top: 24px;
  margin-bottom: 24px;
  padding: 0;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

export const SectionTitle = styled.h4`
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const TicketOrderCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 18px;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.25);
  transition: all 0.25s ease-in-out;

  &:hover {
    border-color: rgba(255, 255, 255, 0.25);
    background: rgba(255, 255, 255, 0.07);
  }
`;

export const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

export const EventInfo = styled.div`
  flex: 1;
  min-width: 220px;
`;

export const FeteName = styled.h5`
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 4px;
`;

export const EventMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
`;

export const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const StatusBadge = styled(Badge)<{ $status?: string }>`
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 20px;
  letter-spacing: 0.8px;
  text-transform: uppercase;

  ${(props) => {
    switch (props.$status) {
      case "PAID":
      case "CONFIRMED":
      case "SUCCESS":
        return `
          background-color: rgba(40, 167, 69, 0.2) !important;
          color: #28a745 !important;
          border: 1px solid rgba(40, 167, 69, 0.4);
        `;
      case "HELD":
      case "PAYING":
      case "PAID_SYNC_PENDING":
        return `
          background-color: rgba(255, 193, 7, 0.2) !important;
          color: #ffc107 !important;
          border: 1px solid rgba(255, 193, 7, 0.4);
        `;
      case "RELEASED":
      case "EXPIRED":
      case "FAILED":
        return `
          background-color: rgba(220, 53, 69, 0.2) !important;
          color: #dc3545 !important;
          border: 1px solid rgba(220, 53, 69, 0.4);
        `;
      default:
        return `
          background-color: rgba(108, 117, 125, 0.2) !important;
          color: #adb5bd !important;
          border: 1px solid rgba(108, 117, 125, 0.4);
        `;
    }
  }}
`;

export const LinesTable = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

export const LineRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  &:last-child {
    border-bottom: none;
  }
`;

export const LineTier = styled.div`
  font-weight: 600;
  color: #f8f9fa;
  font-size: 14px;
`;

export const LineDetail = styled.div`
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
`;

export const LinePrice = styled.div`
  font-weight: 600;
  color: #ffffff;
  font-size: 14px;
`;

export const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  margin-top: 4px;
`;

export const TotalLabel = styled.span`
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
  font-size: 15px;
`;

export const TotalAmount = styled.span`
  font-weight: 800;
  font-size: 18px;
  color: #28a745;
`;

export const TicketTokensList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  margin-bottom: 12px;
`;

export const TicketToken = styled.span`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  color: #e0e0e0;
  font-family: monospace;
`;

export const OrderActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
`;

export const ViewTicketsBtn = styled(Button)`
  font-size: 13px;
  font-weight: 600;
  padding: 8px 18px;
  border-radius: 8px;
  background-color: #28a745;
  border-color: #28a745;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease-in-out;

  &:hover, &:focus {
    background-color: #218838;
    border-color: #1e7e34;
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.35);
  }
`;
